import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { FormControl } from "react-bootstrap";
import { FiCalendar, FiPlus, FiTrash2, FiUpload, FiDownload } from "react-icons/fi";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import DatePicker from "components/Shared/DatePicker";
import SubmitButton from "components/SubmitButton";
import { employeesService } from "services";
import * as rulesActions from "actions/rulesActions";
import * as groupsActions from "actions/groupsActions";
import * as functionsActions from "actions/functionsActions";
import * as shiftsActions from "actions/shiftsActions";
import "./BulkAddEmployeesModal.scss";
import {
	processBulkRows,
	isFieldDisabled,
	hasCellError,
	getFieldErrorMessage,
	shouldShowRowError,
} from "./bulkAddEmployeesProcessor";
import { isHaddockEnvironment } from "utils/documentUtils";

let _rowCounter = 0;

const newRow = () => ({
	_uid: ++_rowCounter,
	name: "",
	surname: "",
	email: "",
	role: "EMPLOYEE",
	contract_id: 0,
	function_id: 0,
	shift_pattern_id: 0,
	group_id: 0,
	start_day: moment().format("YYYY-MM-DD"),
	errors: {},
	_empId: null,
	steps: {},
	rowError: "",
});

const BulkAddEmployeesModal = ({
	handleClose,
	onSuccess,
	contracts,
	groups,
	select_functions,
	select_shift_patterns,
	getContracts,
	getAllGroups,
	getSelectableFunctions,
	getSelectableShiftPatterns,
}) => {
	const { t } = useTranslation();
	const showShiftPatternColumn = !isHaddockEnvironment();
	const [rows, setRows] = useState(() => [newRow()]);
	const [isLoading, setIsLoading] = useState(false);
	const [globalError, setGlobalError] = useState("");
	const fileInputRef = useRef(null);

	useEffect(() => {
		if (!contracts || contracts.length === 0) getContracts();
		if (!groups || groups.length === 0) getAllGroups();
		if (!select_functions || select_functions.length === 0) getSelectableFunctions();
		if (
			showShiftPatternColumn &&
			(!select_shift_patterns || select_shift_patterns.length === 0)
		) {
			getSelectableShiftPatterns();
		}
	}, []); // load selects once on mount

	const updateRow = useCallback((uid, field, value) => {
		setRows((prev) =>
			prev.map((r) =>
				r._uid === uid
					? {
							...r,
							[field]: value,
							errors: { ...r.errors, [field]: undefined },
					  }
					: r
			)
		);
	}, []);

	const addRow = useCallback(() => {
		setRows((prev) => [...prev, newRow()]);
	}, []);

	const [deletingUid, setDeletingUid] = useState(null);

	const removeRow = useCallback(
		async (uid) => {
			const target = rows.find((r) => r._uid === uid);

			// Si el empleado llegó a crearse (quedó a medio camino), lo borramos
			// también en el backend para no dejar registros huérfanos.
			if (target && target._empId) {
				setDeletingUid(uid);
				try {
					const res = await employeesService.remove(target._empId);
					if (res && res.ok === false) throw new Error("remove failed");
					toastr.success(
						t("bulkAddEmployees.deletedTitle", "Empleado eliminado"),
						""
					);
				} catch {
					toastr.error(
						t("common.error", "Error"),
						t(
							"bulkAddEmployees.deleteError",
							"No se pudo eliminar el empleado."
						)
					);
					setDeletingUid(null);
					return;
				}
				setDeletingUid(null);
			}

			setRows((prev) => {
				if (prev.length > 1) return prev.filter((r) => r._uid !== uid);
				return [newRow()];
			});
		},
		[rows, t]
	);

	const validateRows = () => {
		let valid = true;
		const updated = rows.map((row) => {
			const errors = {};
			if (!row.name.trim()) errors.name = t("common.required");
			if (!row.surname.trim()) errors.surname = t("common.required");
			if (!row.email.trim()) {
				errors.email = t("common.required");
			} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
				errors.email = t("employees.invalidEmail", "Email inválido");
			}
			if (!row.start_day) errors.start_day = t("common.required");
			if (Object.keys(errors).length) valid = false;
			return { ...row, errors };
		});
		setRows(updated);
		return valid;
	};

	const handleSubmit = async () => {
		setGlobalError("");
		if (!validateRows()) return;

		setIsLoading(true);

		const results = await processBulkRows(rows, employeesService, t);

		setIsLoading(false);

		const failedRows = results.filter((r) => r._failed);
		const successCount = results.length - failedRows.length;

		// Refresca el checklist del onboarding en cuanto haya al menos un alta.
		if (successCount > 0 && typeof onSuccess === "function") onSuccess();

		if (failedRows.length === 0) {
			toastr.success(
				t("bulkAddEmployees.successTitle", "Empleados añadidos"),
				t("bulkAddEmployees.successMessage", {
					count: successCount,
					defaultValue: "{{count}} empleado(s) creado(s) correctamente.",
				})
			);
			handleClose();
			return;
		}

		// Éxito parcial (o total fallo): mantenemos el modal abierto y dejamos
		// solo las filas que fallaron, conservando el estado de sus steps.
		setRows(failedRows);
		setGlobalError(
			successCount > 0
				? t("bulkAddEmployees.errorSomeFailed", {
						success: successCount,
						failed: failedRows.length,
						defaultValue:
							"{{success}} empleado(s) creado(s). {{failed}} fila(s) con errores: revisa los campos en rojo y reintenta.",
				  })
				: t(
						"bulkAddEmployees.errorAllFailed",
						"No se pudo crear ningún empleado. Revisa los datos."
				  )
		);
	};

	const handleDownloadTemplate = async () => {
		try {
			const blob = await employeesService.downloadImportTemplate();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "plantilla_empleados.xlsx";
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			toastr.error(t("common.error", "Error"), t("bulkAddEmployees.downloadError", "No se pudo descargar la plantilla."));
		}
	};

	const handleImportClick = () => {
		if (fileInputRef.current) fileInputRef.current.click();
	};

	const handleFileChange = async (e) => {
		const file = e.target.files[0];
		if (!file) return;

		setIsLoading(true);
		try {
			const formData = new FormData();
			formData.append("filename", file);
			const prepared = await employeesService.prepareImport(formData);

			if (prepared.ok && !prepared.has_errors && prepared.token) {
				const result = await employeesService.runImport(prepared.token);
				if (result.ok) {
					toastr.success(t("employees.import.success", "Importación completada"), "");
					if (typeof onSuccess === "function") onSuccess();
					handleClose();
				} else {
					setGlobalError(t("employees.import.errorGeneric", "Error en la importación."));
				}
			} else {
				setGlobalError(t("employees.import.errorGeneric", "Error procesando el archivo."));
			}
		} catch {
			setGlobalError(t("employees.import.errorGeneric", "Error en la importación."));
		} finally {
			setIsLoading(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	};

	const contractOptions = (contracts || []).map((c) => (
		<option key={c.id} value={c.id}>{c.name}</option>
	));

	const functionOptions = (select_functions || []).map((f) => (
		<option key={f.id} value={f.id}>{f.name}</option>
	));

	const shiftPatternOptions = (select_shift_patterns || []).map((sp) => (
		<option key={sp.id} value={sp.id}>{sp.name}</option>
	));

	const groupOptions = (groups || []).map((g) => (
		<option key={g.id} value={g.id}>{g.name}</option>
	));

	// Hay filas que ya se intentaron y quedaron con algún step pendiente/fallido.
	const hasFailures = rows.some((r) =>
		Object.values(r.steps || {}).some(
			(s) => s === "failed" || s === "notsent"
		)
	);

	return (
		<>
			<div
				className={`modal-body bulk-add-employees${
					showShiftPatternColumn ? "" : " bulk-add-employees--no-shift-pattern"
				}`}
			>
				<div className="bulk-add-employees__list-header">
					<span className="bulk-add-employees__section-title">
						{t("bulkAddEmployees.sectionTitle", "Empleados")}
					</span>
					<button
						type="button"
						className="btn bulk-add-employees__add-btn"
						onClick={addRow}
					>
						<FiPlus aria-hidden="true" />
						{t("bulkAddEmployees.addRow", "Añadir empleado")}
					</button>
				</div>

				<div className="bulk-add-employees__table-wrap">
					<div className="bulk-add-employees__rows">
						{rows.map((row) => (
							<BulkEmployeeRow
								key={row._uid}
								row={row}
								contractOptions={contractOptions}
								functionOptions={functionOptions}
								shiftPatternOptions={shiftPatternOptions}
								showShiftPatternColumn={showShiftPatternColumn}
								groupOptions={groupOptions}
								onUpdate={updateRow}
								onRemove={removeRow}
								isDeleting={deletingUid === row._uid}
								disabled={isLoading}
								t={t}
							/>
						))}
					</div>
				</div>

				<div className="bulk-add-employees__toolbar">
					<input
						ref={fileInputRef}
						type="file"
						accept=".xlsx,.xls,.csv"
						className="bulk-add-employees__file-input"
						onChange={handleFileChange}
					/>
					<button
						type="button"
						className="btn btn-outline-secondary bulk-add-employees__toolbar-btn"
						onClick={handleImportClick}
						disabled={isLoading}
					>
						<FiUpload aria-hidden="true" />
						{t("bulkAddEmployees.importExcel", "Importar desde Excel")}
					</button>
					<button
						type="button"
						className="btn btn-outline-secondary bulk-add-employees__toolbar-btn"
						onClick={handleDownloadTemplate}
						disabled={isLoading}
					>
						<FiDownload aria-hidden="true" />
						{t("bulkAddEmployees.downloadTemplate", "Descargar plantilla")}
					</button>
				</div>

				{globalError && (
					<div className="alert alert-danger mt-3 mb-0">{globalError}</div>
				)}
			</div>

			<div className="modal-footer">
				<button
					type="button"
					className="btn btn-outline-primary"
					onClick={handleClose}
					disabled={isLoading}
				>
					{t("common.cancel", "Cancelar")}
				</button>
				<SubmitButton
					type="button"
					isLoading={isLoading}
					onClick={handleSubmit}
					text={
						hasFailures
							? t("bulkAddEmployees.retry", "Reintentar")
							: t("bulkAddEmployees.submit", "Añadir empleados")
					}
					loadingText={t("common.saving", "Guardando...")}
				/>
			</div>
		</>
	);
};

const BulkEmployeeRow = ({
	row,
	contractOptions,
	functionOptions,
	shiftPatternOptions,
	showShiftPatternColumn = true,
	groupOptions,
	onUpdate,
	onRemove,
	isDeleting,
	disabled,
	t,
}) => {
	const handleChange = (e) => onUpdate(row._uid, e.target.name, e.target.value);

	const handleDateChange = (date) => {
		onUpdate(row._uid, "start_day", date ? moment(date).format("YYYY-MM-DD") : "");
	};

	const cellError = (field) => hasCellError(row, field);
	const fieldMsg = (field) => getFieldErrorMessage(row, field, t);
	const fieldDisabled = (field) => disabled || isFieldDisabled(row, field, disabled);

	return (
		<div className="bulk-add-employees__row">
			<div className={`bulk-add-employees__cell bulk-add-employees__col--name ${cellError("name") ? "has-error" : ""}`}>
				<label className="bulk-add-employees__field-label">
					{t("bulkAddEmployees.colName", "Nombre")}{" "}
					<span className="label-required">*</span>
				</label>
				<FormControl
					type="text"
					name="name"
					value={row.name}
					onChange={handleChange}
					placeholder={t("bulkAddEmployees.colName", "Nombre")}
					isInvalid={cellError("name")}
					disabled={fieldDisabled("name")}
					size="sm"
				/>
				{(row.errors.name || fieldMsg("name")) && (
					<div className="bulk-add-employees__field-error">
						{row.errors.name || fieldMsg("name")}
					</div>
				)}
			</div>

			<div className={`bulk-add-employees__cell bulk-add-employees__col--surname ${cellError("surname") ? "has-error" : ""}`}>
				<label className="bulk-add-employees__field-label">
					{t("bulkAddEmployees.colSurname", "Apellidos")}{" "}
					<span className="label-required">*</span>
				</label>
				<FormControl
					type="text"
					name="surname"
					value={row.surname}
					onChange={handleChange}
					placeholder={t("bulkAddEmployees.colSurname", "Apellidos")}
					isInvalid={cellError("surname")}
					disabled={fieldDisabled("surname")}
					size="sm"
				/>
				{(row.errors.surname || fieldMsg("surname")) && (
					<div className="bulk-add-employees__field-error">
						{row.errors.surname || fieldMsg("surname")}
					</div>
				)}
			</div>

			<div className={`bulk-add-employees__cell bulk-add-employees__col--email ${cellError("email") ? "has-error" : ""}`}>
				<label className="bulk-add-employees__field-label">
					{t("bulkAddEmployees.colEmail", "Email")}{" "}
					<span className="label-required">*</span>
				</label>
				<FormControl
					type="email"
					name="email"
					value={row.email}
					onChange={handleChange}
					placeholder="nombre@empresa.com"
					isInvalid={cellError("email")}
					disabled={fieldDisabled("email")}
					size="sm"
				/>
				{fieldMsg("email") && (
					<div className="bulk-add-employees__field-error">{fieldMsg("email")}</div>
				)}
			</div>

			<div className={`bulk-add-employees__cell bulk-add-employees__col--contract ${cellError("contract_id") ? "has-error" : ""}`}>
				<label className="bulk-add-employees__field-label">
					{t("bulkAddEmployees.colContract", "Contrato")}
				</label>
				<FormControl
					as="select"
					name="contract_id"
					value={row.contract_id}
					onChange={handleChange}
					isInvalid={cellError("contract_id")}
					disabled={fieldDisabled("contract_id")}
					size="sm"
				>
					<option value={0}>—</option>
					{contractOptions}
				</FormControl>
				{fieldMsg("contract_id") && (
					<div className="bulk-add-employees__field-error">{fieldMsg("contract_id")}</div>
				)}
			</div>

			<div className={`bulk-add-employees__cell bulk-add-employees__col--function ${cellError("function_id") ? "has-error" : ""}`}>
				<label className="bulk-add-employees__field-label">
					{t("bulkAddEmployees.colFunction", "Función")}
				</label>
				<FormControl
					as="select"
					name="function_id"
					value={row.function_id}
					onChange={handleChange}
					isInvalid={cellError("function_id")}
					disabled={fieldDisabled("function_id")}
					size="sm"
				>
					<option value={0}>—</option>
					{functionOptions}
				</FormControl>
				{fieldMsg("function_id") && (
					<div className="bulk-add-employees__field-error">{fieldMsg("function_id")}</div>
				)}
			</div>

			{showShiftPatternColumn && (
				<div className={`bulk-add-employees__cell bulk-add-employees__col--shift-pattern ${cellError("shift_pattern_id") ? "has-error" : ""}`}>
					<label className="bulk-add-employees__field-label">
						{t("bulkAddEmployees.colShiftPattern", "Rueda de turnos")}
					</label>
					<FormControl
						as="select"
						name="shift_pattern_id"
						value={row.shift_pattern_id}
						onChange={handleChange}
						isInvalid={cellError("shift_pattern_id")}
						disabled={fieldDisabled("shift_pattern_id")}
						size="sm"
					>
						<option value={0}>—</option>
						{shiftPatternOptions}
					</FormControl>
					{fieldMsg("shift_pattern_id") && (
						<div className="bulk-add-employees__field-error">{fieldMsg("shift_pattern_id")}</div>
					)}
				</div>
			)}

			<div className={`bulk-add-employees__cell bulk-add-employees__col--group ${cellError("group_id") ? "has-error" : ""}`}>
				<label className="bulk-add-employees__field-label">
					{t("bulkAddEmployees.colGroup", "Grupo")}
				</label>
				<FormControl
					as="select"
					name="group_id"
					value={row.group_id}
					onChange={handleChange}
					isInvalid={cellError("group_id")}
					disabled={fieldDisabled("group_id")}
					size="sm"
				>
					<option value={0}>—</option>
					{groupOptions}
				</FormControl>
				{fieldMsg("group_id") && (
					<div className="bulk-add-employees__field-error">{fieldMsg("group_id")}</div>
				)}
			</div>

			<div className={`bulk-add-employees__cell bulk-add-employees__col--date ${cellError("start_day") ? "has-error" : ""}`}>
				<label className="bulk-add-employees__field-label">
					{t("bulkAddEmployees.colHireDate", "Fecha de contratación")}{" "}
					<span className="label-required">*</span>
				</label>
				<div className="bulk-add-employees__date-wrap">
					<DatePicker
						selected={row.start_day ? moment(row.start_day).toDate() : null}
						onChange={handleDateChange}
						disabled={fieldDisabled("start_day")}
						className={`form-control form-control-sm${cellError("start_day") ? " is-invalid" : ""}`}
						placeholderText="dd/mm/aaaa"
					/>
					<FiCalendar className="bulk-add-employees__date-icon" aria-hidden="true" />
				</div>
				{row.errors.start_day && (
					<div className="bulk-add-employees__field-error">{row.errors.start_day}</div>
				)}
			</div>

			<div className="bulk-add-employees__cell bulk-add-employees__col--actions">
				<button
					type="button"
					className="btn bulk-add-employees__remove-btn"
					onClick={() => onRemove(row._uid)}
					disabled={disabled || isDeleting}
					aria-label={t("bulkAddEmployees.removeRow", "Eliminar fila")}
				>
					<FiTrash2 aria-hidden="true" />
				</button>
			</div>

			{shouldShowRowError(row) && (
				<div className="bulk-add-employees__row-error">{row.rowError}</div>
			)}
		</div>
	);
};

BulkAddEmployeesModal.propTypes = {
	handleClose: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
	contracts: PropTypes.array,
	groups: PropTypes.array,
	select_functions: PropTypes.array,
	select_shift_patterns: PropTypes.array,
	getContracts: PropTypes.func.isRequired,
	getAllGroups: PropTypes.func.isRequired,
	getSelectableFunctions: PropTypes.func.isRequired,
	getSelectableShiftPatterns: PropTypes.func.isRequired,
};

BulkEmployeeRow.propTypes = {
	row: PropTypes.object.isRequired,
	contractOptions: PropTypes.array.isRequired,
	functionOptions: PropTypes.array.isRequired,
	shiftPatternOptions: PropTypes.array,
	showShiftPatternColumn: PropTypes.bool,
	groupOptions: PropTypes.array.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	isDeleting: PropTypes.bool,
	disabled: PropTypes.bool,
	t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
	contracts: state.rulesReducer?.contracts,
	groups: state.groupsReducer?.groups,
	select_functions: state.functionsReducer?.select_functions,
	select_shift_patterns: state.shiftsReducer?.select_shift_patterns,
});

const mapDispatchToProps = (dispatch) => ({
	getContracts: () => dispatch(rulesActions.getContracts()),
	getAllGroups: () => dispatch(groupsActions.getAllGroups()),
	getSelectableFunctions: () => dispatch(functionsActions.getSelectableFunctions()),
	getSelectableShiftPatterns: () => dispatch(shiftsActions.getSelectableShiftPatterns()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkAddEmployeesModal);
